import React, { useState, useEffect } from 'react';
import { X, Cookie } from 'lucide-react';

const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Check if user has already accepted/declined cookies
    const cookieConsent = localStorage.getItem('neupath_cookie_consent');
    if (!cookieConsent) {
      // Small delay to avoid banner appearing immediately
      setTimeout(() => setShowBanner(true), 1000);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('neupath_cookie_consent', 'accepted');
    setShowBanner(false);
    
    // Enable Google Analytics (it's already loaded, just track the consent)
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted'
      });
    }
  };

  const handleDecline = () => {
    localStorage.setItem('neupath_cookie_consent', 'declined');
    setShowBanner(false);
    
    // Disable Google Analytics tracking
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'denied'
      });
    }
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white shadow-2xl border-t border-gray-200 transform transition-transform duration-500 ease-out">
      <div className="max-w-7xl mx-auto p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-start gap-3 flex-1">
            <Cookie className="text-teal-600 mt-1 flex-shrink-0" size={24} />
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">We use cookies</h3>
              <p className="text-sm text-gray-600">
                We use cookies to enhance your experience and analyze our website traffic. 
                By continuing to use this site, you consent to our use of cookies. 
                Learn more in our{' '}
                <a href="/privacy-policy" className="text-teal-600 hover:underline">
                  Privacy Policy
                </a>.
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={handleDecline}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition"
            >
              Decline
            </button>
            <button
              onClick={handleAccept}
              className="px-6 py-2 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 transition"
            >
              Accept
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Simple cookie banner without decline option
export const SimpleCookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const cookieConsent = localStorage.getItem('neupath_cookie_consent');
    if (!cookieConsent) {
      setTimeout(() => setShowBanner(true), 1000);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('neupath_cookie_consent', 'accepted');
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-md z-50 bg-slate-900 text-white p-4 rounded-lg shadow-2xl">
      <button
        onClick={handleAccept}
        className="absolute top-2 right-2 text-gray-400 hover:text-white"
      >
        <X size={20} />
      </button>
      <div className="pr-8">
        <p className="text-sm mb-3">
          We use cookies to enhance your experience. By continuing to visit this site you agree to our use of cookies.
        </p>
        <div className="flex items-center gap-3">
          <button
            onClick={handleAccept}
            className="px-4 py-1.5 bg-white text-slate-900 text-sm font-medium rounded hover:bg-gray-100 transition"
          >
            Got it
          </button>
          <a 
            href="/privacy-policy" 
            className="text-sm text-gray-300 hover:text-white hover:underline"
          >
            Learn more
          </a>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;